// CTSK-525 — the Masonry/Grid/List + Cols + Previews + Scaled bar, shared by the Documents
// list, search results and Watching. Style: "Modern System (Compact)" — flat 2px-radius
// clusters, 11px type, icon + label layout buttons, mono Cols, dot-indicator toggles.
import type { CardViewState } from './useCardView.js';

export type CardViewBarState = Pick<
  CardViewState,
  'wide' | 'savedMode' | 'mode' | 'setMode' | 'cols' | 'setCols' | 'previewAll' | 'setPreviewAll' | 'scaled' | 'setScaled'
>;

const ICON = { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'aria-hidden': true } as const;

function ModeIcon({ mode }: { mode: 'masonry' | 'grid' | 'list' }) {
  if (mode === 'masonry') {
    return (
      <svg {...ICON}>
        <rect x="3" y="3" width="7" height="11" rx="0.5" strokeWidth="2" />
        <rect x="14" y="3" width="7" height="6" rx="0.5" strokeWidth="2" />
        <rect x="14" y="13" width="7" height="8" rx="0.5" strokeWidth="2" />
        <rect x="3" y="17" width="7" height="4" rx="0.5" strokeWidth="2" />
      </svg>
    );
  }
  if (mode === 'grid') {
    return (
      <svg {...ICON}>
        <rect x="4" y="4" width="6" height="6" rx="1.5" strokeWidth="2" />
        <rect x="14" y="4" width="6" height="6" rx="1.5" strokeWidth="2" />
        <rect x="4" y="14" width="6" height="6" rx="1.5" strokeWidth="2" />
        <rect x="14" y="14" width="6" height="6" rx="1.5" strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg {...ICON}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

export function CardViewBar({ v }: { v: CardViewBarState }) {
  if (!v.wide) return null;
  return (
    <div className="docview-bar">
      <div className="docview-toggle" role="group" aria-label="Document view">
        {(['masonry', 'grid', 'list'] as const).map((m) => (
          <button key={m} type="button" aria-pressed={v.savedMode === m} onClick={() => v.setMode(m)}>
            <ModeIcon mode={m} />
            <span>{m === 'masonry' ? 'Masonry' : m === 'grid' ? 'Grid' : 'List'}</span>
          </button>
        ))}
      </div>
      {v.mode !== 'list' && (
        <>
          <div className="docview-toggle docview-toggle--mono" role="group" aria-label="Columns" title="Columns">
            {([null, 2, 3, 4] as const).map((c) => (
              <button key={String(c)} type="button" aria-pressed={v.cols === c} onClick={() => v.setCols(c)}>
                {c === null ? 'AUTO' : c}
              </button>
            ))}
          </div>
          <div className="docview-flags">
            <button type="button" className="docview-flag" aria-pressed={v.previewAll} onClick={() => v.setPreviewAll(!v.previewAll)}>
              <span className="docview-flag__dot" aria-hidden="true" />
              <span>Previews</span>
            </button>
            <button type="button" className="docview-flag" aria-pressed={v.scaled}
              title="Show previews as scaled-down pages instead of near-actual size"
              onClick={() => v.setScaled(!v.scaled)}>
              <span className="docview-flag__dot" aria-hidden="true" />
              <span>Scaled</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
